/**
 * Wait for a specified duration
 */
export function wait(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Wait for a condition to be true
 */
export async function waitFor(
  condition: () => boolean | Promise<boolean>,
  options: {
    timeout?: number;
    interval?: number;
    timeoutMessage?: string;
  } = {}
): Promise<void> {
  const {
    timeout = 5000,
    interval = 100,
    timeoutMessage = 'Timeout waiting for condition'
  } = options;

  const startTime = Date.now();

  while (true) {
    const result = await condition();
    if (result) {
      return;
    }

    if (Date.now() - startTime >= timeout) {
      throw new Error(timeoutMessage);
    }

    await wait(interval);
  }
}

/**
 * Wait for an element to exist in the DOM
 */
export async function waitForElement<T extends HTMLElement>(
  selector: string,
  timeout = 5000
): Promise<T | null> {
  const startTime = Date.now();

  while (Date.now() - startTime < timeout) {
    const element = document.querySelector<T>(selector);
    if (element) {
      return element;
    }
    await wait(100);
  }

  return null;
}

/**
 * Wait for page load
 */
export function waitForPageLoad(timeout = 30000): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.readyState === 'complete') {
      resolve();
      return;
    }

    const timeoutId = setTimeout(() => {
      window.removeEventListener('load', onLoad);
      reject(new Error('Timeout waiting for page load'));
    }, timeout);

    const onLoad = () => {
      clearTimeout(timeoutId);
      resolve();
    };

    window.addEventListener('load', onLoad, { once: true });
  });
}

/**
 * Wait for navigation to complete
 */
export function waitForNavigation(currentUrl?: string, timeout = 30000): Promise<void> {
  return new Promise((resolve, reject) => {
    const startUrl = currentUrl || window.location.href;
    const timeoutId = setTimeout(() => {
      reject(new Error('Timeout waiting for navigation'));
    }, timeout);

    const checkNavigation = () => {
      if (window.location.href !== startUrl && document.readyState === 'complete') {
        clearTimeout(timeoutId);
        clearInterval(intervalId);
        resolve();
      }
    };

    const intervalId = setInterval(checkNavigation, 100);
  });
}

/**
 * Debounce a function
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: number | null = null;

  return function(this: any, ...args: Parameters<T>) {
    if (timeout !== null) {
      clearTimeout(timeout);
    }

    timeout = window.setTimeout(() => {
      func.apply(this, args);
    }, wait);
  };
}

/**
 * Throttle a function
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle = false;

  return function(this: any, ...args: Parameters<T>) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}
